/* eslint-disable brace-style */
import {
  Component,
  OnInit,
  OnDestroy,
  ErrorHandler,
  inject,
  signal,
  computed,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  Validators,
} from '@angular/forms';
import { map, takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { interval, Subject } from 'rxjs';
import { clearFormArray, calculateTimeLeft } from 'src/app/_helpers/utils';
import { hasExperimentEditAccess } from 'src/app/_helpers/experiment-utils';
import { Device } from 'src/app/_models/device';
import { ErrorObject } from 'src/app/_models/error';
import { Experiment } from 'src/app/_models/experiment';
import {
  ExperimentData,
  IDevice,
  IFlask,
} from 'src/app/_models/experiment-data';
import { ExperimentService } from 'src/app/_services/experiment.service';
import { IconNames } from 'src/app/_components/icons/icon-names';
import { ModalService } from 'src/app/_services/modal.service';
import { DeviceService } from 'src/app/_services/device.service';
import { ExperimentStatus } from 'src/app/_models/experiment-status';
import { DeviceSetupStatus } from 'src/app/_models/device-setup-status';
import { DataService } from 'src/app/_services/data.service';
import { TimeComponent } from 'src/app/_models/time-component';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { CsvService } from 'src/app/_services/csv.service';
import { RoleCheckPipe } from '../../_helpers/role-check.pipe';
import { MatIcon } from '@angular/material/icon';
import { ValidationModalComponent } from '../../_components/modals/validation-modal/validation-modal.component';
import { GraphCheckboxGroupComponent } from '../../_components/selection-controls/graph-checkbox-group/graph-checkbox-group.component';
import { CheckboxComponent } from '../../_components/selection-controls/checkbox/checkbox.component';
import { LoadingSpinnerComponent } from '../../_components/loading-spinner/loading-spinner.component';
import { ReducedSmallDataCardComponent } from '../../_components/cards/reduced-small-data-card/reduced-small-data-card.component';
import { StatusIndicatorComponent } from '../../_components/chips/status-indicator/status-indicator.component';
import { DeviceInfoCardComponent } from '../../_components/cards/device-info-card/device-info-card.component';
import { IconButtonComponent } from '../../_components/buttons/icon-button/icon-button.component';
import { CheckboxGroupComponent } from '../../_components/selection-controls/checkbox-group/checkbox-group.component';
import { AsyncPipe, DecimalPipe } from '@angular/common';
import { OptionTabToggleComponent } from '../../_components/selection-controls/option-tab-toggle/option-tab-toggle.component';
import { OxygenExperimentGraphComponent } from 'src/app/_components/graph/oxygen-experiment-graph/oxygen-experiment-graph.component';
import { FluoroExperimentGraphComponent } from 'src/app/_components/graph/fluoro-experiment-graph/fluoro-experiment-graph.component';
import { PlotlyGraphComponent } from 'src/app/_components/graph/plotly-graph/plotly-graph.component';
import { ErrorPageComponent } from 'src/app/_components/error-page/error-page.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatIconButtonComponent } from 'src/app/_components/buttons/mat-icon-button/mat-icon-button.component';
import { ToastComponent } from 'src/app/_components/toast/toast.component';
import { NotesParameterRanges } from 'src/app/_helpers/notes-min-max';
import { Checkbox } from 'src/app/_models/checkbox';
import {
  ExperimentMode,
  ExperimentModeDisplay,
} from 'src/app/_models/experiment-mode';
import { FormBase } from 'src/app/_models/form-base';
import { Laboratory } from 'src/app/_models/laboratory';
import { OGI_ADMIN_AND_SUPER_USER_ROLE_LIST, Role } from 'src/app/_models/role';
import { TabOptions } from 'src/app/_models/tab-options';
import { User } from 'src/app/_models/user';

@Component({
  selector: 'app-display-experiment',
  templateUrl: './display-experiment.component.html',
  styleUrl: './display-experiment.component.scss',
  imports: [
    OptionTabToggleComponent,
    CheckboxGroupComponent,
    IconButtonComponent,
    DeviceInfoCardComponent,
    StatusIndicatorComponent,
    RouterLink,
    ReducedSmallDataCardComponent,
    PlotlyGraphComponent,
    OxygenExperimentGraphComponent,
    FluoroExperimentGraphComponent,
    LoadingSpinnerComponent,
    CheckboxComponent,
    GraphCheckboxGroupComponent,
    ValidationModalComponent,
    AsyncPipe,
    DecimalPipe,
    ErrorPageComponent,
    MatMenuModule,
    MatButtonModule,
    MatIcon,
    MatIconButtonComponent,
    RoleCheckPipe,
    ToastComponent,
  ],
})
export class DisplayExperimentComponent implements OnInit, OnDestroy {
  private readonly csvService = inject(CsvService);
  protected route = inject(ActivatedRoute);
  protected formBuilder = inject(FormBuilder);
  protected experimentService = inject(ExperimentService);
  protected modalService = inject(ModalService);
  protected deviceService = inject(DeviceService);
  protected dataService = inject(DataService);
  protected router = inject(Router);
  protected authenticationService = inject(AuthenticationService);
  private readonly errorHandler = inject(ErrorHandler);

  private csvErrorToast?: ReturnType<typeof setTimeout>;

  //Routing
  navOptions: TabOptions[];
  experimentId: number;
  // Permissions
  laboratory: Laboratory;
  currentUser = signal<User | null>(null);
  // Make role enum accessible in html template
  Role = Role;
  readonly ogiAdminAndSuperUserRoles = OGI_ADMIN_AND_SUPER_USER_ROLE_LIST;
  readonly hasEditAccess = computed(() =>
    hasExperimentEditAccess(this.currentUser()?.role),
  );
  deviceCheckboxesAreInitialised = false;

  // experiment to be displayed
  experiment: Experiment;
  ExperimentStatus = ExperimentStatus;
  ExperimentMode = ExperimentMode;
  ExperimentModeDisplay = ExperimentModeDisplay;
  minNoteLength = NotesParameterRanges.EXPERIMENT_NOTES.minNoteLength;
  maxNoteLength = NotesParameterRanges.EXPERIMENT_NOTES.maxNoteLength;

  // Device controls
  devices: Device[] = [];

  // Lists to track devices to be displayed in the summary section
  selectedDevices: Array<Device> = [];

  // CSV download
  isDownloadingBaseModuleCsv = false;
  isDownloadingTurbidostatCsv = false;
  isDownloadingTemperatureCsv = false;
  isDownloadingOxygenCsv = false;
  isDownloadingFluorescenceCsv = false;
  isDownloadingPhCsv = false;

  // Graph controls
  graphWidth: number;
  graphHeight: number;

  // OD controls
  flasks = ['OD_A', 'OD_B', 'OD_C', 'OD_D'];
  renderODGraph = false;
  selectedODFlasks: Array<string> = [];

  // Temperature controls
  temperatureFlasks = ['TFlask_A', 'TFlask_B', 'TFlask_C', 'TFlask_D'];
  renderTemperatureGraph = false;
  selectedTemperatureFlasks: Array<string> = [];

  // Oxygen controls
  oxygenFlasks = ['A', 'B', 'C', 'D'];
  renderOxygenGraph = false;
  selectedOxygenFlasks: Array<string> = [];

  // pH controls
  flasksPH = ['PHA', 'PHB', 'PHC', 'PHD'];
  renderPHGraph = false;
  selectedPHFlasks: Array<string> = [];

  // Fluorescence controls
  renderFluorescenceGraph = false; // toggle to render graph if update is called
  isFetchingFluorescenceData = false; // toggle to render graph when fluoro button is pressed
  selectedFluorescenceFlasks: Array<string> = [];

  fluorescenceFlasks = ['A', 'B', 'C', 'D'];

  // Device summary form declarations
  deviceCheckboxesField = new FormBase({
    key: 'devices',
    label: 'Select device(s)',
    type: 'checkbox',
    placeholder: 'checkbox',
    disabled: false,
    required: false,
    value: '',
    options: [
      {
        key: '',
        value: '',
      },
    ],
  });

  deviceSelectorForm = this.formBuilder.group({
    [this.deviceCheckboxesField.key]: new FormArray([]),
  });

  // Graph checkbox form declarations
  odFlaskCheckboxesField = new FormBase({
    key: 'odFlasks',
    label: '',
    type: 'checkbox',
    placeholder: '',
    disabled: false,
    required: false,
    value: '',
    options: [
      {
        key: '',
        value: '',
      },
    ],
  });

  isLogarithmic = new Checkbox(
    'Logarithmic',
    'Graph OD logarithmically?',
    'done',
  );

  temperatureFlaskCheckboxesField = new FormBase({
    key: 'temperatureFlasks',
    label: '',
    type: 'checkbox',
    placeholder: '',
    disabled: false,
    required: false,
    value: '',
    options: [
      {
        key: '',
        value: '',
      },
    ],
  });

  oxygenFlaskCheckboxesField = new FormBase({
    key: 'oxygenFlasks',
    label: '',
    type: 'checkbox',
    placeholder: '',
    disabled: false,
    required: false,
    value: '',
    options: [
      {
        key: '',
        value: '',
      },
    ],
  });

  pHFlaskCheckboxesField = new FormBase({
    key: 'pHFlasks',
    label: '',
    type: 'checkbox',
    placeholder: '',
    disabled: false,
    required: false,
    value: '',
    options: [
      {
        key: '',
        value: '',
      },
    ],
  });

  fluorescenceFlaskCheckboxesField = new FormBase({
    key: 'fluorescenceFlasks',
    label: '',
    type: 'checkbox',
    placeholder: '',
    disabled: false,
    required: false,
    value: '',
    options: [
      {
        key: '',
        value: '',
      },
    ],
  });

  graphCheckboxesForm = this.formBuilder.group({
    [this.odFlaskCheckboxesField.key]: new FormArray([]),
    [this.isLogarithmic.id]: [false, Validators.required],
    [this.temperatureFlaskCheckboxesField.key]: new FormArray([]),
    [this.oxygenFlaskCheckboxesField.key]: new FormArray([]),
    [this.fluorescenceFlaskCheckboxesField.key]: new FormArray([]),
    [this.pHFlaskCheckboxesField.key]: new FormArray([]),
  });

  unsubscribe$ = new Subject<void>();

  // Control vars
  error: ErrorObject = null;
  csvError: ErrorObject = null;
  getExperimentError: ErrorObject = null;
  dataDownloadError: ErrorObject = null;
  unsupportedModeError: string | null = null;
  loadingDeviceSummary = false;
  displayToast = false;
  // OD loading vars
  loadingOpticalDensity = false;
  opticalDensityLoadingText = 'Loading data...';
  isLogarithmicChecked = false;
  // Temperature loading vars
  loadingTemperature = false;
  temperatureLoadingText = 'Loading data...';
  // Oxygen loading vars
  loadingOxygen = false;
  oxygenLoadingText = 'Loading data...';
  // Fluorescence loading vars
  loadingFluorescence = false;
  fluorescenceLoadingText = 'Please select an LED and Channel to display data';
  updateSpinner = IconNames.Sync;
  // pH loading vars
  loadingPH = false;
  pHLoadingText = 'Loading data...';
  DeviceSetupStatus = DeviceSetupStatus;

  deviceListLastUpdated = new Date();

  pauseDeviceTitleText = '';
  pauseDeviceValidationText = '';
  pauseDeviceButtonText = '';

  // Pause/restart device selected
  selectedDeviceId: number = null;

  // Countdown controls for time left display
  currentTime$ = interval(1000).pipe(map(() => new Date()));

  // OD Graph
  odGraphId = 'odGraph';
  opticalDensityData: ExperimentData;
  selectedODData: ExperimentData;
  odDeviceTitles = [];

  // Temperature graph
  temperatureGraphId = 'temperatureGraph';
  temperatureData: ExperimentData;
  selectedTemperatureData: ExperimentData;
  temperatureDeviceTitles = [];

  // Oxygen graph
  oxygenGraphId = 'oxygenGraph';
  oxygenData: ExperimentData;
  oxygenDeviceTitles = [];
  selectedOxygenData: ExperimentData;

  // pH graph
  pHGraphId = 'pHGraph';
  pHData: ExperimentData;
  pHDeviceTitles = [];
  selectedPHData: ExperimentData;

  fluorescenceGraphId = 'fluorescenceGraph';
  fluorescenceData: ExperimentData;
  selectedFluorescenceData: ExperimentData;
  fluorescenceDeviceTitles = [];

  ngOnInit(): void {
    // Grab experiment ID from route
    this.route.params
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((params: Params) => {
        this.experimentId = parseInt(params.id as string, 10);
        this.navOptions = [
          {
            value: 'display',
            labelText: 'Display',
            link: '/experiment/' + this.experimentId.toString() + '/display',
          },
          {
            value: 'control',
            labelText: 'Control',
            link: '/experiment/' + this.experimentId.toString() + '/control',
          },
          {
            value: 'device-errors',
            labelText: 'Device Errors',
            link:
              '/experiment/' + this.experimentId.toString() + '/device-errors',
          },
        ];
      });

    this.authenticationService.currentUser$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((user: User) => {
        this.currentUser.set(user);
      });

    // Now let's get the experiment from the API
    this.getExperiment(false, true);
  }

  getExperiment(updateDevicesOnly = false, setDefaultData = false): void {
    this.getExperimentError = null;
    this.loadingDeviceSummary = true;

    this.experimentService.getExperimentById(this.experimentId).subscribe({
      next: (response: Experiment) => {
        if (
          response.experimentStatusId !== ExperimentStatus.Draft &&
          response.experimentStatusId !== ExperimentStatus.DraftLocked
        ) {
          this.deviceListLastUpdated = new Date();
          this.loadingDeviceSummary = false;
          this.experiment = response;
          this.devices = response.devices;

          const updatedSelectedDevices: Device[] = [];

          this.selectedDevices?.forEach((selectedDevice: Device) => {
            const device = this.devices.find(
              (device) => device.id === selectedDevice.id,
            );
            if (device) updatedSelectedDevices.push(device);
          });

          if (this.devices.length === 1 && this.selectedDevices.length === 0) {
            updatedSelectedDevices.push(this.devices[0]);
          }

          this.selectedDevices = updatedSelectedDevices;

          this.initialiseDeviceCheckboxes(this.devices);
          this.initialiseODFlaskCheckboxes(this.devices, setDefaultData);
          this.initialiseTemperatureFlaskCheckboxes(
            this.devices,
            setDefaultData,
          );
          if (this.experiment.useOxygen) {
            this.initialiseOxygenFlaskCheckboxes(this.devices, setDefaultData);
          }
          if (this.experiment.usePH) {
            this.initialisePHFlaskCheckboxes(this.devices, setDefaultData);
          }
          this.initialiseFluorescenceFlaskCheckboxes(
            this.devices,
            setDefaultData,
          );
          // Now we grab the experiment data, calling it in tandem with getExperiment() causes null
          // exception on backend
          if (!updateDevicesOnly && this.experiment.isDeleted === false) {
            this.getExperimentData();
          }
        } else {
          this.loadingDeviceSummary = false;
          this.getExperimentError = {
            status: -1,
            message: 'Experiment is still in draft status',
            code: null,
          };
        }
      },
      error: (error: ErrorObject) => {
        this.getExperimentError = error;
        this.loadingDeviceSummary = false;
      },
    });
  }

  // Check if all flasks are empty (no data) - used to determine if graph should be displayed
  private allFlasksEmpty(devices: IDevice[]): boolean {
    return devices.every((device) =>
      device.flasks.every(
        (flask) =>
          !flask.values ||
          flask.values.length === 0 ||
          flask.values.every((v) => v === null),
      ),
    );
  }

  getExperimentData(): void {
    this.unsupportedModeError = null;
    const knownModes = Object.values(ExperimentMode).filter(
      (v): v is number => typeof v === 'number',
    );
    if (!knownModes.includes(this.experiment.experimentMode)) {
      // Report so engineers learn about backend/frontend experiment-mode drift.
      this.errorHandler.handleError(
        new Error(
          `Unsupported experimentMode "${this.experiment.experimentMode}" for experiment ${this.experimentId}`,
        ),
      );
      this.unsupportedModeError =
        'This experiment uses a mode that isn’t supported by this version of the app. Please refresh to update, or contact support if the problem persists.';
      return;
    }

    this.loadingOpticalDensity = true;
    this.loadingTemperature = true;
    this.loadingOxygen = true;
    this.loadingPH = true;
    // First reset the current experiment data list
    this.opticalDensityData = null;
    this.dataService
      .getBaseModuleData(this.experimentId, this.experiment.experimentMode)
      .subscribe({
        next: (response) => {
          if (
            response.devices.length > 0 &&
            !this.allFlasksEmpty(response.devices)
          ) {
            this.opticalDensityData = response;
            this.opticalDensityLoadingText =
              'Data loaded, select a flask to display';
            this.setSelectedODData();
          } else {
            this.opticalDensityLoadingText =
              'No data available yet, please wait...';
          }
        },
        error: (error: ErrorObject) => {
          this.error = error;
          this.opticalDensityLoadingText = 'Error loading data';
        },
      })
      .add(() => {
        this.getTemperatureData();
        this.loadingOpticalDensity = false;
      });
  }

  getTemperatureData(): void {
    // First reset the current temperature data list
    this.temperatureData = null;
    this.dataService
      .getTemperatureModuleData(this.experimentId)
      .subscribe({
        next: (response) => {
          if (
            response.devices.length > 0 &&
            !this.allFlasksEmpty(response.devices)
          ) {
            this.temperatureData = response;
            this.temperatureLoadingText =
              'Data loaded, select a flask to display';
            this.setSelectedTempData();
          } else {
            this.temperatureLoadingText =
              'No data available yet, please wait...';
          }
        },
        error: (error: ErrorObject) => {
          this.error = error;
          this.temperatureLoadingText = 'Error loading data';
        },
      })
      .add(() => {
        if (this.experiment.useOxygen) {
          this.getOxygenData();
        } else if (this.experiment.usePH) {
          this.getPHData();
        } else if (this.experiment.useFluorescence) {
          this.getFluorescenceData();
        }
        this.loadingTemperature = false;
      });
  }

  getOxygenData(): void {
    // First reset the current oxygen data list
    this.oxygenData = null;
    // Set the filter to null to fetch all data (it can be 'true', 'false', or null)
    const filter = null as unknown as string;

    this.dataService
      .getOxygenModuleData(this.experimentId, filter)
      .subscribe({
        next: (response) => {
          if (
            response.devices.length > 0 &&
            !this.allFlasksEmpty(response.devices)
          ) {
            this.oxygenData = response;
            this.oxygenLoadingText = 'Data loaded, select a flask to display';
            this.setSelectedOxygenData();
          } else {
            this.oxygenData = null;
            this.selectedOxygenData = null;
            this.oxygenLoadingText = 'No data available yet, please wait...';
          }
        },
        error: (error: ErrorObject) => {
          this.error = error;
          this.oxygenLoadingText = 'Error loading data';
        },
      })
      .add(() => {
        if (this.experiment.usePH) {
          this.getPHData();
        } else if (this.experiment.useFluorescence) {
          this.getFluorescenceData();
        }
        this.loadingOxygen = false;
      });
  }

  getPHData(): void {
    // First reset the current pH data list
    this.pHData = null;
    this.dataService
      .getPhModuleData(this.experimentId)
      .subscribe({
        next: (response) => {
          if (
            response.devices.length > 0 &&
            !this.allFlasksEmpty(response.devices)
          ) {
            this.pHData = response;
            this.loadingPH = false;
            this.pHLoadingText = 'Data loaded, select a flask to display';
            this.setSelectedPhData();
          } else {
            this.loadingPH = false;
            this.pHLoadingText = 'No data available yet, please wait...';
          }
        },
        error: (error: ErrorObject) => {
          this.error = error;
          this.pHLoadingText = 'Error loading data';
          this.loadingPH = false;
        },
      })
      .add(() => {
        if (this.experiment.useFluorescence) {
          this.getFluorescenceData();
        } else {
          this.loadingFluorescence = false;
        }
      });
  }

  getFluorescenceData(): void {
    this.dataService.getFluorescenceModuleData(this.experimentId).subscribe({
      next: (response) => {
        if (
          response.devices.length > 0 &&
          !this.allFlasksEmpty(response.devices)
        ) {
          this.fluorescenceData = response;
          this.fluorescenceLoadingText =
            'Data loaded, select a flask to display';
          this.setSelectedFluoroData();
        } else {
          this.fluorescenceLoadingText =
            'No data available yet, please wait...';
        }
        this.loadingFluorescence = false;
        this.isFetchingFluorescenceData = false;
      },
      error: (error: ErrorObject) => {
        this.error = error;
        this.fluorescenceLoadingText = 'Error loading data';
        this.isFetchingFluorescenceData = false;
        this.loadingFluorescence = false;
      },
    });
  }

  downloadBaseModuleCsv(isExtended: boolean): void {
    this.csvError = null;
    this.displayToast = false;
    this.isDownloadingBaseModuleCsv = true;

    this.csvService
      .downloadModuleCsv('base', this.experimentId, isExtended)
      .subscribe({
        next: () => {
          this.isDownloadingBaseModuleCsv = false;
        },
        error: (error: ErrorObject) => {
          this.csvError = error;
          this.isDownloadingBaseModuleCsv = false;
          this.showToast();
        },
      });
  }

  downloadTurbidostatCsv(isExtended: boolean): void {
    this.csvError = null;
    this.displayToast = false;
    this.isDownloadingTurbidostatCsv = true;

    this.csvService
      .downloadModuleCsv('turbidostat', this.experimentId, isExtended)
      .subscribe({
        next: () => {
          this.isDownloadingTurbidostatCsv = false;
        },
        error: (error: ErrorObject) => {
          this.csvError = error;
          this.isDownloadingTurbidostatCsv = false;
          this.showToast();
        },
      });
  }

  downloadTemperatureCsv(isExtended: boolean): void {
    this.csvError = null;
    this.displayToast = false;
    this.isDownloadingTemperatureCsv = true;

    this.csvService
      .downloadModuleCsv('temperature', this.experimentId, isExtended)
      .subscribe({
        next: () => {
          this.isDownloadingTemperatureCsv = false;
        },
        error: (error: ErrorObject) => {
          this.csvError = error;
          this.isDownloadingTemperatureCsv = false;
          this.showToast();
        },
      });
  }

  downloadOxygenCsv(isExtended: boolean): void {
    this.csvError = null;
    this.displayToast = false;
    this.isDownloadingOxygenCsv = true;

    this.csvService
      .downloadModuleCsv('oxygen', this.experimentId, isExtended)
      .subscribe({
        next: () => {
          this.isDownloadingOxygenCsv = false;
        },
        error: (error: ErrorObject) => {
          this.csvError = error;
          this.isDownloadingOxygenCsv = false;
          this.showToast();
        },
      });
  }

  downloadFluorescenceCsv(type: string): void {
    this.csvError = null;
    this.displayToast = false;
    this.isDownloadingFluorescenceCsv = true;

    this.csvService.downloadFluoroModuleCsv(type, this.experimentId).subscribe({
      next: () => {
        this.isDownloadingFluorescenceCsv = false;
      },
      error: (error: ErrorObject) => {
        this.csvError = error;
        this.isDownloadingFluorescenceCsv = false;
        this.showToast();
      },
    });
  }

  downloadPhCsv(isExtended: boolean): void {
    this.csvError = null;
    this.displayToast = false;
    this.isDownloadingPhCsv = true;
    this.csvService
      .downloadModuleCsv('ph', this.experimentId, isExtended)
      .subscribe({
        next: () => {
          this.isDownloadingPhCsv = false;
        },
        error: (error: ErrorObject) => {
          this.csvError = error;
          this.isDownloadingPhCsv = false;
          this.showToast();
        },
      });
  }

  /**
   * @description Initialises the device checkboxes used to select which devices to display
   * in the Device Summary view
   * @param {Device[]} deviceList list of devices attached to the current experiment from the API
   */
  initialiseDeviceCheckboxes(deviceList: Device[]): void {
    // Reset checkbox list
    this.deviceCheckboxesField.options = [];
    // Reset checkbox form array
    clearFormArray(this.checkboxesFormArray);
    // Now construct the checkbox array with new values
    deviceList.forEach((device) => {
      this.deviceCheckboxesField.options.push({
        key: 'device_' + device.id.toString(),
        value: device.name,
      });
      // If the device is in the selected devices list, set to true, otherwise false
      if (this.selectedDevices?.find((x) => x.id === device.id)) {
        this.checkboxesFormArray.push(new FormControl(true));
      } else {
        this.checkboxesFormArray.push(new FormControl(false));
      }
    });
    this.deviceCheckboxesAreInitialised = true;
  }

  /**
   * @description Initialises the flask checkboxes used to select which flasks to display the optical density data for on the graph
   * @param {Device[]} deviceList list of devices attached to the current experiment from the API
   */
  initialiseODFlaskCheckboxes(
    deviceList: Device[],
    setDefaultData: boolean,
  ): void {
    // Reset checkbox list
    this.odFlaskCheckboxesField.options = [];
    this.flasks.sort();
    // Reset checkbox form array
    clearFormArray(this.odFlaskCheckboxesFormArray);
    // Now construct the checkbox array with new values
    deviceList.forEach((device, i) => {
      this.flasks.forEach((flask) => {
        this.odDeviceTitles.push(device.name);
        this.odFlaskCheckboxesField.options.push({
          key: 'device_' + device.id.toString() + '_flask_' + flask,
          value: `OD ${flask.slice(-1)}`,
        });
        // Set default data selection (first device - 4 flasks)
        if (i === 0 && setDefaultData) {
          this.selectedODFlasks.push(
            'device_' + device.id.toString() + '_flask_' + flask,
          );
        }
        // If the device is in the selected devices list, set to true, otherwise false
        if (
          this.selectedODFlasks?.find(
            (x) => x === 'device_' + device.id.toString() + '_flask_' + flask,
          )
        ) {
          this.odFlaskCheckboxesFormArray.push(new FormControl(true));
        } else {
          this.odFlaskCheckboxesFormArray.push(new FormControl(false));
        }
      });
    });
    this.odDeviceTitles = this.odDeviceTitles.filter((x, i) => i % 4 === 4 - 1);
  }

  /**
   *  @description Initialises the flask checkboxes used to select which flasks to display the temperature data for on the graph
   * @param {Device[]} deviceList list of devices attached to the current experiment from the API
   */
  initialiseTemperatureFlaskCheckboxes(
    deviceList: Device[],
    setDefaultData: boolean,
  ): void {
    // Reset checkbox list
    this.temperatureFlaskCheckboxesField.options = [];
    this.temperatureFlasks.sort();
    // Reset checkbox form array
    clearFormArray(this.temperatureFlaskCheckboxesFormArray);
    // Now construct the checkbox array with new values
    deviceList.forEach((device, i) => {
      this.temperatureFlasks.forEach((flask) => {
        this.temperatureDeviceTitles.push(device.name);
        this.temperatureFlaskCheckboxesField.options.push({
          key: 'device_' + device.id.toString() + '_flask_' + flask,
          value: `Temp ${flask.slice(-1)}`,
        });
        // Set default data selection (first device - 4 flasks)
        if (i === 0 && setDefaultData) {
          this.selectedTemperatureFlasks.push(
            'device_' + device.id.toString() + '_flask_' + flask,
          );
        }
        // If the device is in the selected devices list, set to true, otherwise false
        if (
          this.selectedTemperatureFlasks?.find(
            (x) => x === 'device_' + device.id.toString() + '_flask_' + flask,
          )
        ) {
          this.temperatureFlaskCheckboxesFormArray.push(new FormControl(true));
        } else {
          this.temperatureFlaskCheckboxesFormArray.push(new FormControl(false));
        }
      });
    });
    this.temperatureDeviceTitles = this.temperatureDeviceTitles.filter(
      (x, i) => i % 4 === 4 - 1,
    );
  }

  initialiseOxygenFlaskCheckboxes(
    deviceList: Device[],
    setDefaultData: boolean,
  ): void {
    // reset checkbox list
    this.oxygenFlaskCheckboxesField.options = [];
    this.oxygenFlasks.sort();
    // reset checkbox form array
    clearFormArray(this.oxygenFlaskCheckboxesFormArray);
    // now construct the checkbox array with new values
    deviceList.forEach((device, i) => {
      this.oxygenFlasks.forEach((flask) => {
        this.oxygenDeviceTitles.push(device.name);
        this.oxygenFlaskCheckboxesField.options.push({
          key: 'device_' + device.id.toString() + '_flask_' + flask,
          value: `Oxygen ${flask.slice(-1)}`,
        });
        // Set default data selection (first device - 4 flasks)
        if (i === 0 && setDefaultData) {
          this.selectedOxygenFlasks.push(
            'device_' + device.id.toString() + '_flask_' + flask,
          );
        }
        // if the device is in the selected devices list, set to true, otherwise false
        if (
          this.selectedOxygenFlasks?.find(
            (x) => x === 'device_' + device.id.toString() + '_flask_' + flask,
          )
        ) {
          this.oxygenFlaskCheckboxesFormArray.push(new FormControl(true));
        } else {
          this.oxygenFlaskCheckboxesFormArray.push(new FormControl(false));
        }
      });
    });
    this.oxygenDeviceTitles = this.oxygenDeviceTitles.filter(
      (x, i) => i % 4 === 4 - 1,
    );
  }

  initialisePHFlaskCheckboxes(
    deviceList: Device[],
    setDefaultData: boolean,
  ): void {
    // reset checkbox list
    this.pHFlaskCheckboxesField.options = [];
    this.flasksPH.sort();
    // reset checkbox form array
    clearFormArray(this.pHFlaskCheckboxesFormArray);
    // now construct the checkbox array with new values
    deviceList.forEach((device, i) => {
      this.flasksPH.forEach((flask) => {
        this.pHDeviceTitles.push(device.name);
        this.pHFlaskCheckboxesField.options.push({
          key: 'device_' + device.id.toString() + '_flask_' + flask,
          value: `pH ${flask.slice(-1)}`,
        });
        // Set default data selection (first device - 4 flasks)
        if (i === 0 && setDefaultData) {
          this.selectedPHFlasks.push(
            'device_' + device.id.toString() + '_flask_' + flask,
          );
        }
        // if the device is in the selected devices list, set to true, otherwise false
        if (
          this.selectedPHFlasks?.find(
            (x) => x === 'device_' + device.id.toString() + '_flask_' + flask,
          )
        ) {
          this.pHFlaskCheckboxesFormArray.push(new FormControl(true));
        } else {
          this.pHFlaskCheckboxesFormArray.push(new FormControl(false));
        }
      });
    });
    this.pHDeviceTitles = this.pHDeviceTitles.filter((x, i) => i % 4 === 4 - 1);
  }

  initialiseFluorescenceFlaskCheckboxes(
    deviceList: Device[],
    setDefaultData: boolean,
  ): void {
    // reset checkbox list
    this.fluorescenceFlaskCheckboxesField.options = [];
    this.fluorescenceFlasks.sort();
    // reset checkbox form array
    clearFormArray(this.fluorescenceFlaskCheckboxesFormArray);
    // now construct the checkbox array with new values
    deviceList.forEach((device, i) => {
      this.fluorescenceFlasks.forEach((flask) => {
        this.fluorescenceDeviceTitles.push(device.name);
        this.fluorescenceFlaskCheckboxesField.options.push({
          key: 'device_' + device.id.toString() + '_flask_' + flask,
          value: `${flask}`,
        });

        // Set default data selection (first device - 4 flasks)
        if (i === 0 && setDefaultData) {
          this.selectedFluorescenceFlasks.push(
            'device_' + device.id.toString() + '_flask_' + flask,
          );
        }

        // if the device is in the selected devices list, set to true, otherwise false
        if (
          this.selectedFluorescenceFlasks?.find(
            (x) => x === 'device_' + device.id.toString() + '_flask_' + flask,
          )
        ) {
          this.fluorescenceFlaskCheckboxesFormArray.push(new FormControl(true));
        } else {
          this.fluorescenceFlaskCheckboxesFormArray.push(
            new FormControl(false),
          );
        }
      });
    });
    this.fluorescenceDeviceTitles = this.fluorescenceDeviceTitles.filter(
      (x, i) => i % 4 === 4 - 1,
    );
  }

  /**
   * @description Sets selected optical density data
   */
  setSelectedODData(): void {
    this.selectedODData = {
      dataType: 'Optical Density',
      devices: [],
      experimentMode: this.experiment.experimentMode,
    };

    // This is a hacky way to deep copy the devices array and prevent it being passed by reference
    const devices: IDevice[] = JSON.parse(
      JSON.stringify(this.opticalDensityData?.devices),
    ) as IDevice[];

    devices.forEach((device) => {
      device.flasks = [
        { name: 'OD_A' },
        { name: 'OD_B' },
        { name: 'OD_C' },
        { name: 'OD_D' },
      ] as IFlask[]; // Start with an empty flask array for each device
    });

    this.opticalDensityData.devices.forEach((device, deviceIndex) => {
      device.flasks?.forEach((flask) => {
        const index = this.selectedODFlasks?.findIndex(
          (selectedFlask) =>
            selectedFlask ===
            `device_${device.deviceId.toString()}_flask_${flask.name}`,
        );
        if (index !== -1) {
          const flaskIndex = device.flasks.findIndex(
            (f) => f.name === flask.name,
          );
          devices[deviceIndex].flasks[flaskIndex] = flask;
        }
      });
    });
    this.selectedODData.devices = devices;
    this.renderODGraph = true;
  }

  /**
   * @description Sets selected temperature data
   */
  setSelectedTempData(): void {
    this.selectedTemperatureData = {
      dataType: 'Temperature',
      devices: [],
    };

    const devices: IDevice[] = JSON.parse(
      JSON.stringify(this.temperatureData?.devices),
    ) as IDevice[];

    devices.forEach((device) => {
      device.flasks = [
        { name: 'TFlask_A' },
        { name: 'TFlask_B' },
        { name: 'TFlask_C' },
        { name: 'TFlask_D' },
      ] as IFlask[]; // Start with an empty flask array for each device
    });

    this.temperatureData.devices.forEach((device, deviceIndex) => {
      device.flasks?.forEach((flask) => {
        const index = this.selectedTemperatureFlasks?.findIndex(
          (selectedFlask) =>
            selectedFlask ===
            `device_${device.deviceId.toString()}_flask_${flask.name}`,
        );
        if (index !== -1) {
          const flaskIndex = device.flasks.findIndex(
            (f) => f.name === flask.name,
          );
          devices[deviceIndex].flasks[flaskIndex] = flask;
        }
      });
    });
    this.selectedTemperatureData.devices = devices;
    this.renderTemperatureGraph = true;
  }

  setSelectedOxygenData(): void {
    this.selectedOxygenData = {
      dataType: 'Oxygen',
      devices: [],
    };

    const devices: IDevice[] = JSON.parse(
      JSON.stringify(this.oxygenData?.devices),
    ) as IDevice[];

    devices.forEach((device) => {
      device.flasks = []; // Start with an empty flask array for each device
    });

    this.oxygenData.devices.forEach((device, deviceIndex) => {
      device.flasks?.forEach((flask) => {
        // Get last character of flask.name i.e A,B,C or D
        const lastChar = flask.name[flask.name.length - 1];
        const index = this.selectedOxygenFlasks?.findIndex(
          (selectedFlask) =>
            selectedFlask ===
            `device_${device.deviceId.toString()}_flask_${lastChar}`,
        );
        if (index !== -1) {
          const flaskIndex = device.flasks.findIndex(
            (f) => f.name === flask.name,
          );
          devices[deviceIndex].flasks[flaskIndex] = flask;
        }
      });
    });
    this.selectedOxygenData.devices = devices;
    this.renderOxygenGraph = true;
  }

  /**
   * @description Sets selected pH data
   */
  setSelectedPhData(): void {
    this.selectedPHData = {
      dataType: 'pH',
      devices: [],
    };

    const devices: IDevice[] = JSON.parse(
      JSON.stringify(this.pHData?.devices),
    ) as IDevice[];

    devices.forEach((device) => {
      device.flasks = [
        { name: 'PHA' },
        { name: 'PHB' },
        { name: 'PHC' },
        { name: 'PHD' },
      ] as IFlask[]; // Start with an empty flask array for each device
    });

    this.pHData.devices.forEach((device, deviceIndex) => {
      device.flasks?.forEach((flask) => {
        const index = this.selectedPHFlasks?.findIndex(
          (selectedFlask) =>
            selectedFlask ===
            `device_${device.deviceId.toString()}_flask_${flask.name}`,
        );
        if (index !== -1) {
          const flaskIndex = device.flasks.findIndex(
            (f) => f.name === flask.name,
          );
          devices[deviceIndex].flasks[flaskIndex] = flask;
        }
      });
    });
    this.selectedPHData.devices = devices;
    this.renderPHGraph = true;
  }

  /**
   * @description Sets selected fluorescence data
   */
  setSelectedFluoroData(): void {
    // Update the selected fluorescence data object
    this.selectedFluorescenceData = {
      dataType: 'Fluorescence',
      devices: [],
    };

    // Create a deep copy of the devices and reset their flasks
    const devices: IDevice[] = JSON.parse(
      JSON.stringify(this.fluorescenceData?.devices),
    ) as IDevice[];

    devices.forEach((device) => {
      device.flasks = [];
    });

    // Iterate over each device and flask to populate the selected devices
    this.fluorescenceData.devices.forEach((device, deviceIndex) => {
      device.flasks?.forEach((flask) => {
        // Get last character of flask.name i.e A,B,C or D
        const lastChar = flask.name[flask.name.length - 1];
        const index = this.selectedFluorescenceFlasks?.findIndex(
          (selectedFlask) =>
            selectedFlask ===
            `device_${device.deviceId.toString()}_flask_${lastChar}`,
        );
        if (index !== -1) {
          const flaskIndex = device.flasks.findIndex(
            (f) => f.name === flask.name,
          );
          devices[deviceIndex].flasks[flaskIndex] = flask;
        }
      });
    });
    // Update selected fluorescence data with devices that have flasks
    this.selectedFluorescenceData.devices = devices;
    this.renderFluorescenceGraph = true;
  }

  // ET - the below checkbox funcs could be refactored into a generic function
  // if a discriminator was passed with the click event to indicate which form array
  // the click came from

  /**
   * @description Handles the click event from the device summary checkboxes, and updates
   * the selectedDevices array accordingly
   * @param {Event} event The click event from the device checkbox
   */
  deviceCheckboxClicked(event: Event): void {
    const target = event.target as HTMLInputElement;
    // Find the id of the device that was clicked
    this.deviceCheckboxesField.options.forEach((element) => {
      if (element.key === target.name) {
        const cleanId = element.key.replace('device_', '');

        // Ugly but findIndex fails to find the index of the device in the array
        // may be related to the fact that the devices are nested objects when created (shallow copy?)
        let index = -1;
        for (const [i, device] of this.selectedDevices?.entries()) {
          if (device.id.toString() === cleanId) {
            index = i;
            break;
          }
        }

        if (target.checked) {
          // If device is not already in the list, push it, push it good
          if (index === -1) {
            this.selectedDevices.push(
              this.devices.filter((x) => x.id.toString() === cleanId)[0],
            );
          }
        } else {
          // If device is in the list, pop it, pop it good
          if (index !== -1) {
            this.selectedDevices.splice(index, 1);
          }
        }
      }
    });
  }

  // checkbox functions
  handleFlaskCheckboxClicked(event: Event, type: string): void {
    switch (type) {
      case 'od':
        this.flaskCheckboxClicked(event);
        break;
      case 'temperature':
        this.temperatureFlaskCheckboxClicked(event);
        break;
      case 'oxygen':
        this.oxygenFlaskCheckboxClicked(event);
        break;
      case 'fluorescence':
        this.fluorescenceFlaskCheckboxClicked(event);
        break;
      case 'pH':
        this.pHFlaskCheckboxClicked(event);
        break;
    }
  }

  handleLogarithmicCheckboxClicked(event: boolean): void {
    this.isLogarithmicChecked = event;

    this.renderODGraph = false;
    setTimeout(() => {
      this.logarithmicCheckboxClicked();
    }, 10);
  }

  flaskCheckboxClicked(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.odFlaskCheckboxesField.options.forEach((element, i) => {
      if (element.key === target.name) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.graphCheckboxesForm.value[this.odFlaskCheckboxesField.key][i] =
          target.checked;
        const index = this.selectedODFlasks?.findIndex(
          (flask) => flask === element.key,
        );
        if (target.checked) {
          // If device is not already in the list, push it, push it good
          if (index === -1) {
            this.selectedODFlasks.push(element.key);
          }
        } else {
          // If device is in the list, pop it, pop it good
          if (index !== -1) {
            this.selectedODFlasks.splice(index, 1);
          }
        }
      }
    });

    this.setSelectedODData();
  }

  logarithmicCheckboxClicked(): void {
    this.renderODGraph = true;
  }

  temperatureFlaskCheckboxClicked(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.temperatureFlaskCheckboxesField.options.forEach((element, i) => {
      if (element.key === target.name) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.graphCheckboxesForm.value[
          this.temperatureFlaskCheckboxesField.key
        ][i] = target.checked;
        const index = this.selectedTemperatureFlasks?.findIndex(
          (flask) => flask === element.key,
        );
        if (target.checked) {
          // If device is not already in the list, push it, push it good
          if (index === -1) {
            this.selectedTemperatureFlasks.push(element.key);
          }
        } else {
          // If device is in the list, pop it, pop it good
          if (index !== -1) {
            this.selectedTemperatureFlasks.splice(index, 1);
          }
        }
      }
    });

    this.setSelectedTempData();
  }

  oxygenFlaskCheckboxClicked(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.oxygenFlaskCheckboxesField.options.forEach((element, i) => {
      if (element.key === target.name) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.graphCheckboxesForm.value[this.oxygenFlaskCheckboxesField.key][i] =
          target.checked;
        const index = this.selectedOxygenFlasks?.findIndex(
          (flask) => flask === element.key,
        );
        if (target.checked) {
          // If device is not already in the list, push it, push it good
          if (index === -1) {
            this.selectedOxygenFlasks.push(element.key);
          }
        } else {
          // If device is in the list, pop it, pop it good
          if (index !== -1) {
            this.selectedOxygenFlasks.splice(index, 1);
          }
        }
      }
    });
    this.setSelectedOxygenData();
  }

  pHFlaskCheckboxClicked(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.pHFlaskCheckboxesField.options.forEach((element, i) => {
      if (element.key === target.name) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.graphCheckboxesForm.value[this.pHFlaskCheckboxesField.key][i] =
          target.checked;
        const index = this.selectedPHFlasks?.findIndex(
          (flask) => flask === element.key,
        );
        if (target.checked) {
          // If device is not already in the list, push it, push it good
          if (index === -1) {
            this.selectedPHFlasks.push(element.key);
          }
        } else {
          // If device is in the list, pop it, pop it good
          if (index !== -1) {
            this.selectedPHFlasks.splice(index, 1);
          }
        }
      }
    });
    this.setSelectedPhData();
  }

  fluorescenceFlaskCheckboxClicked(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.fluorescenceFlaskCheckboxesField.options.forEach((element, i) => {
      if (element.key === target.name) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.graphCheckboxesForm.value[
          this.fluorescenceFlaskCheckboxesField.key
        ][i] = target.checked;
        const index = this.selectedFluorescenceFlasks?.findIndex(
          (flask) => flask === element.key,
        );
        if (target.checked) {
          // If device is not already in the list, push it, push it good
          if (index === -1) {
            this.selectedFluorescenceFlasks.push(element.key);
          }
        } else {
          // If device is in the list, pop it, pop it good
          if (index !== -1) {
            this.selectedFluorescenceFlasks.splice(index, 1);
          }
        }
      }
    });
    this.setSelectedFluoroData();
  }

  showToast() {
    if (this.csvError) {
      clearTimeout(this.csvErrorToast);
    }
    this.displayToast = true;
    this.csvErrorToast = setTimeout(() => {
      this.displayToast = false;
    }, 5000);
  }

  getTimeLeft(
    startTime: Date | string | null | undefined,
    duration: number | null | undefined,
    currentTime: Date,
  ): TimeComponent | null {
    return calculateTimeLeft(startTime, duration, currentTime);
  }

  closeModal(modal: string): void {
    this.modalService.close(modal);
  }

  openModal(modal: string): void {
    this.modalService.open(modal);
  }

  isDevicePaused(): boolean {
    const device = this.experiment.devices.find(
      (device) => device.id === this.selectedDeviceId,
    );
    return device?.deviceStatusId === DeviceSetupStatus.Paused;
  }

  // TO DO - pass isDevicePaused to device info card to determine pause button label?

  pauseRestartButtonClicked(deviceId: number): void {
    this.selectedDeviceId = deviceId;
    const devicePaused = this.isDevicePaused();
    if (devicePaused) {
      this.pauseDeviceTitleText = 'Restart device';
      this.pauseDeviceValidationText = 'restart';
      this.pauseDeviceButtonText = 'restart';
    } else {
      this.pauseDeviceTitleText = 'Pause device';
      this.pauseDeviceValidationText = 'pause';
      this.pauseDeviceButtonText = 'pause';
    }
    this.openModal('pause-device');
  }

  pauseRestartDevices(): void {
    this.deviceService
      .pauseDeviceOnExperiment(
        this.selectedDeviceId.toString(),
        this.experiment.id,
        !this.isDevicePaused(),
      )
      .subscribe({
        next: () => {
          this.error = null;
          this.selectedDeviceId = null;
          this.getExperiment();
          this.closeModal('pause-device');
        },
        error: (error: ErrorObject) => {
          this.error = error;
          this.selectedDeviceId = null;
        },
      });
  }

  get checkboxesFormArray(): FormArray {
    return this.deviceSelectorForm.get('devices') as FormArray;
  }

  get odFlaskCheckboxesFormArray(): FormArray {
    return this.graphCheckboxesForm.get(
      this.odFlaskCheckboxesField.key,
    ) as FormArray;
  }

  get temperatureFlaskCheckboxesFormArray(): FormArray {
    return this.graphCheckboxesForm.get(
      this.temperatureFlaskCheckboxesField.key,
    ) as FormArray;
  }

  get oxygenFlaskCheckboxesFormArray(): FormArray {
    return this.graphCheckboxesForm.get(
      this.oxygenFlaskCheckboxesField.key,
    ) as FormArray;
  }

  get fluorescenceFlaskCheckboxesFormArray(): FormArray {
    return this.graphCheckboxesForm.get(
      this.fluorescenceFlaskCheckboxesField.key,
    ) as FormArray;
  }

  get pHFlaskCheckboxesFormArray(): FormArray {
    return this.graphCheckboxesForm.get(
      this.pHFlaskCheckboxesField.key,
    ) as FormArray;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();

    if (this.csvErrorToast) {
      clearTimeout(this.csvErrorToast);
    }
  }
}
